/**
 * Encryption Failure Log
 *
 * Per-record cache (localStorage) of which fields couldn't be re-encrypted
 * during the auto-migration. The UI uses this to:
 *  - Show a recovery affordance on case/pre-screening detail pages
 *  - Suppress the raw `[Decryption failed]` sentinel in lists (replace with
 *    a usable identifier)
 *  - Aggregate the count for the post-migration toast
 *
 * Background: prior to 2026-05-14, the migration silently dropped fields
 * that failed RSA decryption. Combined with the per-session "ran already"
 * flag, this meant a doctor saw `[Decryption failed]` forever with no way
 * to recover. The log surfaces the failure so the user has a path.
 *
 * @module utils/encryptionFailureLog
 */

const STORAGE_KEY = 'expertmd_encryption_failures_v1';

export type EncryptionFailureKind = 'case' | 'intakeRequest';

interface EncryptionFailureEntry {
  kind: EncryptionFailureKind;
  id: string;
  fields: string[];
  /** ISO timestamp of the last migration run that observed the failure. */
  lastSeenAt: string;
}

type EncryptionFailureLog = Record<string, EncryptionFailureEntry>;

/** Internal: stable key for the localStorage map. */
function entryKey(kind: EncryptionFailureKind, id: string): string {
  return `${kind}:${id}`;
}

function readLog(): EncryptionFailureLog {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as EncryptionFailureLog;
  } catch {
    return {};
  }
}

function writeLog(log: EncryptionFailureLog): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
  } catch (err) {
    // Quota exhausted or storage unavailable — non-fatal; the in-session
    // toast still surfaces the issue. Log to console for diagnostics.
    console.warn('[encryptionFailureLog] Could not persist failure log:', err);
  }
}

/**
 * Record a re-encryption failure for a specific record. Merges with any
 * existing entry — the union of failed fields is preserved across runs.
 */
export function recordEncryptionFailure(
  kind: EncryptionFailureKind,
  id: string,
  fields: string[],
): void {
  if (!id || fields.length === 0) return;
  const log = readLog();
  const key = entryKey(kind, id);
  const existing = log[key];
  const merged = existing ? Array.from(new Set([...existing.fields, ...fields])) : fields.slice();
  log[key] = {
    kind,
    id,
    fields: merged,
    lastSeenAt: new Date().toISOString(),
  };
  writeLog(log);
}

/**
 * Look up which fields (if any) failed for a specific record. Returns []
 * when nothing is recorded for the record — callers can treat as "no
 * recovery prompt needed".
 */
export function getFailedFields(kind: EncryptionFailureKind, id: string): string[] {
  if (!id) return [];
  const log = readLog();
  return log[entryKey(kind, id)]?.fields ?? [];
}

/**
 * Drop the entry for a record after the user manually recovers the
 * field(s). Called by the recovery UI on successful submit.
 */
export function clearEncryptionFailure(
  kind: EncryptionFailureKind,
  id: string,
  fields?: string[],
): void {
  if (!id) return;
  const log = readLog();
  const key = entryKey(kind, id);
  const existing = log[key];
  if (!existing) return;
  if (!fields || fields.length === 0) {
    delete log[key];
  } else {
    const remaining = existing.fields.filter((f) => !fields.includes(f));
    if (remaining.length === 0) {
      delete log[key];
    } else {
      log[key] = { ...existing, fields: remaining };
    }
  }
  writeLog(log);
}

/** Return all records that currently have at least one un-recovered field. */
export function getAllEncryptionFailures(): EncryptionFailureEntry[] {
  const log = readLog();
  return Object.values(log);
}

/** Aggregate summary for the post-migration toast. */
export function summarizeEncryptionFailures(): {
  recordCount: number;
  fieldCount: number;
} {
  const entries = getAllEncryptionFailures();
  return {
    recordCount: entries.length,
    fieldCount: entries.reduce((sum, e) => sum + e.fields.length, 0),
  };
}
